import React, { useEffect, useState } from "react";
import { Grid, makeStyles } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";
import { Alert } from "@material-ui/lab";
import { BaseLayout, Header, Loader, NoData } from "../components";
import * as types from "../ducks/types";
import CardItemWithActions from "../components/CardItemWithActions";
import { getSessionPresentations } from "../ducks/actions";
import { UploadSessionPresentationDialog } from "../features/presentations";
import { useCongress } from "../hooks";
import { ReactComponent as MyPresentationsSvg } from "../assets/icons/my presentations.svg";

const useStyles = makeStyles(theme => ({
  alert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  presentationsList: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const MyPresentationsPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { congress } = useCongress();

  useEffect(() => {
    dispatch(getSessionPresentations());
  }, [dispatch]);

  const {
    data: { session_presentations: sessionPresentations },
    loading: sessionPresentationsLoading
  } = useQuery({
    type: types.GET_SESSION_PRESENTATIONS,
    defaultData: { session_presentations: [] }
  });

  const [sessionPresentationId, setSessionPresentationId] = useState(null);

  const onUploadSessionPresentationDialogClose = () => {
    setSessionPresentationId(null);
  };

  const onUpload = id => {
    setSessionPresentationId(id);
  };

  const deadline = moment.utc(congress.deadline, "YYYY-MM-DD");
  const today = moment().utc().startOf("date");

  const actions = id => {
    return today.isAfter(deadline)
      ? []
      : [
          {
            label: intl.formatMessage({
              id: "Pages.MyPresentationsPage.Upload",
              defaultMessage: "Upload"
            }),
            action: () => onUpload(id)
          }
        ];
  };

  return (
    <BaseLayout>
      <Loader loading={sessionPresentationsLoading}>
        <Header
          icon={MyPresentationsSvg}
          title={intl.formatMessage({
            id: "Pages.MyPresentationsPage.MyPresentations",
            defaultMessage: "My Presentations"
          })}
          helper={intl.formatMessage({
            id: "Pages.MyPresentationsPage.MyPresentationsHelper",
            defaultMessage: "Welcome to your personal area! Here you can upload your Presentations"
          })}
        />

        <NoData
          message={intl.formatMessage({
            id: "Pages.MyPresentationsPage.NoData",
            defaultMessage: "There are no presentations. Check later for new contents..."
          })}
          data={sessionPresentations}
        >
          {congress.deadline &&
            (today.isAfter(deadline) ? (
              <Alert variant="filled" severity="error" className={classes.alert}>
                {intl.formatMessage({
                  id: "Pages.MyPresentationsPage.DeadlineExpired",
                  defaultMessage: "The deadline for uploading the presentations has expired"
                })}
              </Alert>
            ) : (
              <Alert variant="filled" severity="info" className={classes.presentationsList}>
                {intl.formatMessage(
                  {
                    id: "Pages.MyPresentationsPage.DeadlineInfo",
                    defaultMessage: "You can upload the presentations until {deadline}"
                  },
                  { deadline: deadline.format("DD/MM/YYYY") }
                )}
              </Alert>
            ))}
          <Grid container spacing={2} className={classes.alert}>
            {sessionPresentations.map(presentation => (
              <Grid item xs={12} sm={6} md={4} key={presentation.id}>
                <CardItemWithActions
                  title={presentation.title}
                  description={presentation.description}
                  thumbUrl={presentation.file?.preview_url}
                  fileName={presentation.file?.filename}
                  actions={actions(presentation.id)}
                />
              </Grid>
            ))}
          </Grid>
        </NoData>
      </Loader>
      {sessionPresentationId && (
        <UploadSessionPresentationDialog
          id={sessionPresentationId}
          onClose={onUploadSessionPresentationDialogClose}
        />
      )}
    </BaseLayout>
  );
};

export default MyPresentationsPage;
